import { Vervesammendrag, VervetOrdre } from "codegen";
import { format } from "date-fns";
import { debounce } from "lodash";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { faArrowUpRightFromSquare, faSearch, faXmarkCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Direction, Flex, Justify } from "@ntemarked/mittne-components/dist/Flex/Flex";

import LoadingAnimation from "components/organisms/LoadingAnimation";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import { Table } from "components/organisms/Table";
import { ToggleFieldClean } from "components/organisms/form/ToggleField";
import { useQuery } from "hooks/useParamsState";
import useQueryParamsState from "hooks/useQueryParamsState";
import { useStrombestillingApiQuery } from "hooks/useStrombestillingApi";
import { DatePicker } from "pages/Strombestilling/orders/components/DatePicker";
import { OrderFilterTable, StatusType } from "pages/Strombestilling/components/OrderFilterTable";
import { FilterWrapper } from "pages/Strombestilling/orders/components/OrdersFilters";
import { VervingStatus } from "pages/Strombestilling/verving/VervingStatus";
import { getStrombestillingBaseLink, getUcBaseLink } from "utils/Utils";

export function ReferralsPage() {
	const today = new Date();
	const [selectedDate, setSelectedDate] = useState<Date>(today); // Kun for datepicker, ikke queryparam
	const [month, setMonth] = useQueryParamsState("month");
	const [isFullYear, setIsFullYear] = useQueryParamsState("year");
	const [status, setStatus] = useQueryParamsState("status");
	const [customerName, setCustomerName] = useQueryParamsState("search");
	// For å kunne resette søkestrenger når man fjerner filter må søkefeltet ha en egen state, i tillegg til queryparam staten
	// Man kan ikke bruke samme ettersom debouncen legger inn et delay i oppdateringen
	const [searchName, setSearchName] = useState(customerName ?? "");

	function resetFilters() {
		setSelectedDate(today);
		setMonth("");
		setIsFullYear("");
		setStatus("");
		setCustomerName("");
		setSearchName("");
	}

	const anyFilterActivated = Boolean(month ?? isFullYear ?? status ?? customerName);

	const params = useQuery();
	const { data, isLoading, isError } = useStrombestillingApiQuery<Vervesammendrag>(`/vervinger/ordre?${params.toString()}`, ["referrals", params.toString()]);
	const debouncedSetCustomerName = useMemo(() => debounce((newSearch: string) => setCustomerName(newSearch), 200), [setCustomerName]);

	return (
		<Flex style={{ maxWidth: "1200px" }} gap="2rem" direction={Direction.column}>
			<MainLayoutPageHeader title="Vervinger" />
			<Flex direction={Direction.row} justifyContent={Justify.spaceBetween}>
				<Flex direction={Direction.row} gap="20px" alignItems={Justify.center}>
					<DatePicker
						date={selectedDate}
						setDate={(newDate) => {
							setSelectedDate(newDate);
							setMonth(format(newDate, "yyyy-MM"));
						}}
						isFullYear={!!isFullYear}
					/>
					<ToggleFieldClean
						checked={!!isFullYear}
						onChange={() => setIsFullYear(isFullYear ? "" : "true")}
						label="Vis statistikk for hele året"
						wrapperStyle={{ width: "auto" }}
						labelStyle={{ color: "#28292c", fontWeight: 500 }}
					/>
				</Flex>
				{anyFilterActivated && (
					<Link to="" onClick={resetFilters}>
						Nullstill filter
					</Link>
				)}
			</Flex>
			{isError && (
				<Flex gap="1rem" alignItems={Justify.center}>
					<FontAwesomeIcon size="2x" color="darkred" icon={faXmarkCircle} /> <p>Henting av data feilet!</p>
				</Flex>
			)}
			{isLoading && <LoadingAnimation />}
			<Flex direction={Direction.column} gap="1rem" alignItems={Justify.start}>
				{data?.perStatus && (
					<FilterWrapper>
						<OrderFilterTable
							name="Status"
							statusType={StatusType.verving}
							data={data.perStatus}
							selected={status}
							setSelected={setStatus}
							style={{ minHeight: "13rem" }}
						/>
					</FilterWrapper>
				)}
			</Flex>
			<input
				type="text"
				value={searchName}
				onChange={(e) => {
					setSearchName(e.target.value || "");
					debouncedSetCustomerName(e.target.value || "");
				}}
				placeholder="Søk etter bestiller eller verver"
			/>
			{data && <p>Viser {data.totaltAntall} vervinger</p>}
			{data && (
				<Table
					entries={data.verveordre}
					labels={{
						status: "Status",
						sammendrag: "Bestilt av",
						verver: "Vervet av",
						tidligsteUtbetaling: "Tidligste utbetaling",
					}}
					generators={{
						status: (v: VervetOrdre) => <VervingStatus status={v.status!} />,

						verver: (v: VervetOrdre) => (
							<Flex alignItems={Justify.center} gap="0.5rem">
								{v.verver?.navn}
								<a
									onClick={(e) => e.stopPropagation()}
									title="Søk etter appbruker i adminportalen"
									href={`/users?search="${v.verver?.navn}"`}
									target="_blank"
									rel="noreferrer"
								>
									<FontAwesomeIcon icon={faSearch} />
								</a>
								{v.verver?.guid && (
									<a
										onClick={(e) => e.stopPropagation()}
										title="Se verver i UC"
										href={`${getUcBaseLink()}/#/customer/${v.verver.guid}`}
										target="_blank"
										rel="noreferrer"
									>
										<img src="/assets/icons/uc-logo.svg" alt="Kontraktsdetaljer i UtilityCloud" style={{ height: "1rem", width: "auto" }} />
									</a>
								)}
							</Flex>
						),
						tidligsteUtbetaling: (v: VervetOrdre) =>
							new Date(v.tidligsteUtbetaling!).toLocaleDateString("no", { day: "2-digit", month: "2-digit", year: "numeric" }),
					}}
					action={(o: VervetOrdre) => (
						<a
							href={`${getStrombestillingBaseLink()}/admin/ordreoversikt/${o.orderId}`}
							title="Gå til bestillingsdetaljer"
							target="_blank"
							rel="noopener noreferrer"
						>
							<FontAwesomeIcon icon={faArrowUpRightFromSquare} />
						</a>
					)}
				/>
			)}
		</Flex>
	);
}

